import React, { useState, useEffect } from "react";
import { useUser } from "../hooks/useUser";
import { useNavigate } from "react-router-dom";
import "../styles/FeedbackPage.css";

const CATEGORIES = [
  { value: "bug",        label: "Bug Report"},
  { value: "suggestion", label: "Suggestion"},
  { value: "other",      label: "Other"},
];

export default function FeedbackPage() {
  const { user } = useUser();
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setReady(true), 240);
    return () => clearTimeout(id);
  }, []);

  const [form, setForm] = useState({
    category:      "suggestion",
    title:         "",
    message:       "",
    rating:        0,
    email:         "",
    email_consent: false,
    anonymous:     false,
  });

  const [status,   setStatus]   = useState(null);
  const [bodyKey,  setBodyKey]  = useState(0); // null | "loading" | "success" | "error"
  const [errorMsg, setErrorMsg] = useState("");
  const [hoverRating, setHoverRating] = useState(0);

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleSubmit = async () => {
    if (!form.title.trim())   return setErrorMsg("Title is required.");
    if (!form.message.trim()) return setErrorMsg("Message is required.");
    if (form.email && !form.email_consent)
      return setErrorMsg("You must consent to email processing.");

    setErrorMsg("");
    setStatus("loading");

    try {
      const res = await fetch("/api/feedback", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          rating:  form.rating || null,
          user_id: (!form.anonymous && user) ? user.id : null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Something went wrong.");
      setStatus("success");
      setBodyKey(k => k + 1);
    } catch (e) {
      setErrorMsg(e.message);
      setStatus("error");
    }
  };

  if (!ready) return <div className="fb-root" />;

  return (
    <div className="fb-root">
      <div className="fb-card">

        {/* Header */}
        <div className="fb-header">
          <h1 className="fb-header-title">Feedback</h1>
          <p className="fb-header-sub">Report bugs · Share ideas · Say hi</p>
        </div>

        <div className="fb-body" key={bodyKey}>

          {status === "success" ? (
            <div className="fb-success-screen">
              <div className="fb-success-icon">✦</div>
              <div className="fb-success-msg">✔ Your feedback has been recorded. Thank you, champion.</div>
              <button className="btn-gold" onClick={() => navigate("/")}>Back to Home</button>
            </div>
          ) : (
            <>
              {/* Category */}
              <div className="fb-field">
                <label className="fb-label">Category</label>
                <div className="fb-category-row">
                  {CATEGORIES.map(c => (
                    <button
                      key={c.value}
                      className={`fb-category-btn ${form.category === c.value ? "active" : ""}`}
                      onClick={() => set("category", c.value)}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Rating */}
              <div className="fb-field">
                <label className="fb-label">
                  Rating <span className="fb-optional">(optional)</span>
                </label>
                <div className="fb-stars" onMouseLeave={() => setHoverRating(0)}>
                  {[1,2,3,4,5].map(n => (
                    <button
                      key={n}
                      className={`fb-star ${n <= (hoverRating || form.rating) ? "active" : ""}`}
                      onMouseEnter={() => setHoverRating(n)}
                      onClick={() => set("rating", form.rating === n ? 0 : n)}
                    >★</button>
                  ))}
                </div>
              </div>

              {/* Title */}
              <div className="fb-field">
                <label className="fb-label">Title</label>
                <input
                  className="input"
                  type="text"
                  placeholder="Short summary…"
                  value={form.title}
                  maxLength={50}
                  onChange={e => set("title", e.target.value)}
                />
                <span className="fb-counter">{form.title.length} / 50</span>
              </div>

              {/* Message */}
              <div className="fb-field">
                <label className="fb-label">Message</label>
                <textarea
                  className="fb-textarea"
                  placeholder="Describe your feedback in detail…"
                  value={form.message}
                  maxLength={500}
                  rows={5}
                  onChange={e => set("message", e.target.value)}
                />
                <span className="fb-counter">{form.message.length} / 500</span>
              </div>

              {/* Email */}
              <div className="fb-field">
                <label className="fb-label">
                  Email <span className="fb-optional">(optional — if you want a reply)</span>
                </label>
                <input
                  className="input"
                  type="email"
                  placeholder="your@email.com"
                  value={form.email}
                  onChange={e => set("email", e.target.value)}
                />
                {form.email && (
                  <label className="fb-checkbox-row" style={{ marginTop: "0.55rem" }}>
                    <input
                      type="checkbox"
                      className="fb-checkbox"
                      checked={form.email_consent}
                      onChange={e => set("email_consent", e.target.checked)}
                    />
                    <span>I consent to my email being stored solely to respond to this feedback.</span>
                  </label>
                )}
              </div>

              {/* Anonymous — only for logged-in users */}
              {user && (
                <div className="fb-field">
                  <label className="fb-checkbox-row">
                    <input
                      type="checkbox"
                      className="fb-checkbox"
                      checked={form.anonymous}
                      onChange={e => set("anonymous", e.target.checked)}
                    />
                    <span>
                      Submit anonymously <span className="fb-optional">(your account won't be linked)</span>
                    </span>
                  </label>
                </div>
              )}

              {errorMsg && <div className="fb-error">⚠ {errorMsg}</div>}

              <button
                className="btn-gold"
                style={{ width: "100%", marginTop: "0.25rem" }}
                onClick={handleSubmit}
                disabled={status === "loading"}
              >
                {status === "loading" && <span className="loading-spinner" />}
                {status === "loading" ? "Sending…" : "Submit Feedback"}
              </button>
            </>
          )}

        </div>

        <div className="fb-footer">For the Horde · For the Alliance</div>

      </div>
    </div>
  );
}